// CONTRACT:I8-LOCAL-SETTINGS.1.0
/** The COP display-settings SCHEMA — typed + versioned, and the SINGLE SOURCE OF TRUTH for the
 *  setting key names shared with Bundle A (render). These are PURE DISPLAY PREFERENCES: nothing
 *  here may alter WHAT is visible (the projection / PDP outcome) — only HOW it is drawn.
 *
 *  Bundle A (apps/cop/src/host/deckBackend.ts + renderSettingsKeys.ts) consumes `fade.*` and
 *  `advanced.*` by READING this store with fallbacks; to keep the key names from ever drifting,
 *  A imports the shape + DEFAULT_SETTINGS from THIS module (the names live here once). */

/** Schema version. Bump when the shape changes incompatibly; a persisted blob with a different
 *  version is treated as foreign and the affected fields fall back to defaults (never crash).
 *  v2 (2026-07-04): markerMode gains "coalition" (values are additive — persisted v1 blobs stay
 *  valid, NO value migration) + the icon-density-LOD toggles (iconDensityLod /
 *  blueForceAlwaysProminent, both default TRUE). Load-time sanitisation is per-leaf, so a v1
 *  blob's missing keys land on the new defaults by construction.
 *  v3 (2026-07-06): additive alert-acknowledgement leaves (alerts.readIds / alerts.clearedIds —
 *  operator SELF-SCOPE display state, identity-scoped: they only ever write into a persona
 *  scope, never the device scope). v1/v2 blobs stay valid; the missing leaves land on the
 *  (empty-array) defaults by construction.
 *  v4 (2026-07-06): additive IDENTITY leaves (user.callsign / user.useRealName — the operator's
 *  chosen display handle, identity-scoped like alerts.*) + PRESENCE opt-in leaves
 *  (presence.shareLocation, default FALSE — sharing is strictly opt-in — and
 *  presence.updateSeconds, accepted 5..120). v1–v3 blobs stay valid; the missing leaves land
 *  on the defaults by construction. */
export const SETTINGS_SCHEMA_VERSION = 4 as const;

/** Cap on each persisted alert-ack id array (alerts.readIds / alerts.clearedIds) — aligned with
 *  the host's ALERT_LOG_CAP (apps/cop/src/host/cop.ts): the operator log is bounded to 100 rows,
 *  so acknowledgement state beyond that is unreachable display state. Enforced BOTH by the leaf
 *  validator (an over-cap persisted/foreign array is rejected) and at the write site (the host
 *  truncates to the most recent CAP before setSetting). */
export const ALERT_ACK_CAP = 100 as const;

/** Max persisted callsign length (user.callsign) — a map/chat label stays glanceable. */
export const CALLSIGN_MAX_LENGTH = 24 as const;

/** presence.updateSeconds accepted range (the UI slider clamps to the same bounds). */
export const PRESENCE_UPDATE_MIN_S = 5 as const;
export const PRESENCE_UPDATE_MAX_S = 120 as const;

/** A render-profile id is a THEMES key (packages/design-tokens) — e.g. "intel-skin", "tak", "civic". */
export type RenderProfileId = string;
/** Symbology carrier: legacy colour-fill dot ("Minimal") vs MIL-STD-2525 frame SHAPE
 *  ("Standard") vs full milsymbol MIL-STD-2525 symbols ("Coalition"). */
export type MarkerMode = "milsym" | "dot" | "coalition";
/** Device-class layout recipe (mirrors apps/cop host/shellContract LayoutRecipe). */
export type LayoutRecipe = "cockpit" | "field" | "workbench";

/** Affiliation fade opacities (declutter) — Bundle A reads these as the marker fill alpha for
 *  the neutral / unknown affiliations. Both are 0..1. */
export interface FadeSettings {
  /** Neutral-affiliation marker fill opacity. Default 0.20. */
  neutral: number;
  /** Unknown-affiliation marker fill opacity. Default 0.70. */
  unknown: number;
}

/** Velocity-correlated error-ellipse per-domain sigma assumptions (Bundle A's anisotropic smear).
 *  Larger sigma = looser position uncertainty. Ordered tight→loose: ships tight, unknown-air wide. */
export interface EllipseSettings {
  /** Maritime-track position-uncertainty sigma (tight). Default 0.6. */
  shipSigma: number;
  /** Identified-air position-uncertainty sigma (mid). Default 1.5. */
  airSigma: number;
  /** Unknown/hostile-air position-uncertainty sigma (wide). Default 4.0. */
  unknownAirSigma: number;
}

/** Air drop-line (vertical stalk to ground) params — Bundle A gates the always-on stalk on these
 *  to avoid re-clutter, and dims the ground dot / line. */
export interface DropLineSettings {
  /** Only draw drop-lines at/above this map zoom (declutter gate). Default 6. */
  minZoom: number;
  /** Only stalk assets at/above this altitude in metres. Default 1000. */
  minAltitudeM: number;
  /** Ground-dot opacity (0..1). Default 0.30. */
  groundDotOpacity: number;
  /** Stalk-line opacity (0..1). Default 0.30. */
  lineOpacity: number;
}

/** Advanced render params (the SettingsDrawer "Advanced" tab) — jointly owned with Bundle A. */
export interface AdvancedSettings {
  ellipse: EllipseSettings;
  /** Velocity-vector + comet-tail length multiplier on Bundle A's speed-scaled leader. Default 1.0. */
  vectorScale: number;
  dropLine: DropLineSettings;
}

/** Operator alert-ACKNOWLEDGEMENT state (v3) — the ids the operator marked read / dismissed in
 *  the ALERT LOG. This is operator SELF-SCOPE display state (a UI acknowledgement / dismiss),
 *  NOT a security decision: clearing an alert removes it from THAT operator's log/glyph but
 *  never from the registry/map, and can never make a PDP-denied object visible (this store is
 *  display-only by hard invariant). IDENTITY-SCOPED: these leaves refuse to write into the
 *  device scope (a device-scope write would bleed one user's acks into every user) — with no
 *  active persona the write is dropped. Each array is capped at ALERT_ACK_CAP. */
export interface AlertAckSettings {
  /** Envelope ids the operator has marked READ (row de-emphasis in the AlertsPanel). */
  readIds: string[];
  /** Envelope ids the operator has DISMISSED (hidden from the log + excluded from the
   *  VerdictGlyph's face/urgency count — a re-emit re-surfaces the alert, unread). */
  clearedIds: string[];
}

/** Operator IDENTITY display preferences (v4) — how the operator's OWN name is printed (map
 *  label, chat author, presence). DISPLAY-ONLY by hard invariant: the username in the JWT stays
 *  the security subject; a callsign can never change an access decision. IDENTITY-SCOPED (like
 *  alerts.*): these leaves refuse to write into the device scope — one user's handle must never
 *  bleed into every user via the device-fallback merge. */
export interface UserIdentitySettings {
  /** The operator's chosen display handle ("" = unset → surfaces fall back to the roster /
   *  federation display name). Validator: ≤ CALLSIGN_MAX_LENGTH chars, printable, no
   *  control chars. */
  callsign: string;
  /** Opt-in: print the operator's REAL name (roster / Google profile) instead of the callsign
   *  wherever a name is shown. Default false. */
  useRealName: boolean;
}

/** PRESENCE broadcast preferences (v4) — placeholders the presence engine reads (with
 *  fallbacks) when it ships. Location sharing is STRICTLY OPT-IN: shareLocation defaults
 *  FALSE and nothing broadcasts until the operator flips it. */
export interface PresenceSettings {
  /** Broadcast own position to the team picture. Default FALSE (hard opt-in requirement). */
  shareLocation: boolean;
  /** Position-update interval in seconds. Default 15; accepted 5..120 (UI clamps the same). */
  updateSeconds: number;
}

/** The full, typed, versioned settings object. `getSettings()` returns the EFFECTIVE (merged)
 *  value of this shape. */
export interface Settings {
  /** Schema version of this object (always SETTINGS_SCHEMA_VERSION for a live store). */
  version: number;
  /** Active render profile / theme id. */
  renderProfile: RenderProfileId;
  /** Active symbology carrier. */
  markerMode: MarkerMode;
  /** Active device-class layout recipe. */
  layout: LayoutRecipe;
  /** Force-reduced-motion preference (UI-only; mirrors the :root data-force-reduced-motion attr). */
  reducedMotion: boolean;
  /** ICON-DENSITY LOD: when the viewport is dense, lower-priority icons fade + shrink by
   *  affiliation rank (presentation only — a faded icon is still present + pickable). Default true. */
  iconDensityLod: boolean;
  /** Blue force stays full-size/full-opacity under the icon-density LOD. When off, own force
   *  still degrades most gently (rank 1 of 4). Default true. */
  blueForceAlwaysProminent: boolean;
  /** Affiliation fade opacities. */
  fade: FadeSettings;
  /** Advanced render params. */
  advanced: AdvancedSettings;
  /** Operator alert-ack state (identity-scoped; self-scope display state, never a security
   *  decision — see AlertAckSettings). */
  alerts: AlertAckSettings;
  /** Operator identity display preferences (v4; identity-scoped — see UserIdentitySettings). */
  user: UserIdentitySettings;
  /** Presence broadcast preferences (v4; opt-in — see PresenceSettings). */
  presence: PresenceSettings;
}

/** The factory DEFAULT settings — also Bundle A's fallback constants if the store is ever absent.
 *  renderProfile/markerMode/layout match the COP boot (INTEL_SKIN / milsym / cockpit) so a fresh
 *  store agrees with the host at boot. Returned by reference-free deep clone so callers can't
 *  mutate the canonical default. */
export const DEFAULT_SETTINGS: Settings = Object.freeze({
  version: SETTINGS_SCHEMA_VERSION,
  renderProfile: "intel-skin",
  markerMode: "milsym",
  layout: "cockpit",
  reducedMotion: false,
  iconDensityLod: true,
  blueForceAlwaysProminent: true,
  fade: Object.freeze({ neutral: 0.2, unknown: 0.7 }),
  advanced: Object.freeze({
    ellipse: Object.freeze({ shipSigma: 0.6, airSigma: 1.5, unknownAirSigma: 4.0 }),
    vectorScale: 1.0,
    dropLine: Object.freeze({
      minZoom: 6,
      minAltitudeM: 1000,
      groundDotOpacity: 0.3,
      lineOpacity: 0.3,
    }),
  }),
  alerts: Object.freeze({
    readIds: Object.freeze([]) as unknown as string[],
    clearedIds: Object.freeze([]) as unknown as string[],
  }),
  user: Object.freeze({ callsign: "", useRealName: false }),
  presence: Object.freeze({ shareLocation: false, updateSeconds: 15 }),
}) as Settings;

/** A deep, mutable clone of DEFAULT_SETTINGS (the frozen canonical default is never handed out). */
export function defaultSettings(): Settings {
  return {
    version: SETTINGS_SCHEMA_VERSION,
    renderProfile: DEFAULT_SETTINGS.renderProfile,
    markerMode: DEFAULT_SETTINGS.markerMode,
    layout: DEFAULT_SETTINGS.layout,
    reducedMotion: DEFAULT_SETTINGS.reducedMotion,
    iconDensityLod: DEFAULT_SETTINGS.iconDensityLod,
    blueForceAlwaysProminent: DEFAULT_SETTINGS.blueForceAlwaysProminent,
    fade: { ...DEFAULT_SETTINGS.fade },
    advanced: {
      ellipse: { ...DEFAULT_SETTINGS.advanced.ellipse },
      vectorScale: DEFAULT_SETTINGS.advanced.vectorScale,
      dropLine: { ...DEFAULT_SETTINGS.advanced.dropLine },
    },
    alerts: {
      readIds: [...DEFAULT_SETTINGS.alerts.readIds],
      clearedIds: [...DEFAULT_SETTINGS.alerts.clearedIds],
    },
    user: { ...DEFAULT_SETTINGS.user },
    presence: { ...DEFAULT_SETTINGS.presence },
  };
}

// --- The typed setting-PATH registry (the runtime contract for setSetting) ------------------

/** A dotted leaf path into `Settings` — the unit a single control writes via `setSetting`. */
export type SettingPath =
  | "renderProfile"
  | "markerMode"
  | "layout"
  | "reducedMotion"
  | "iconDensityLod"
  | "blueForceAlwaysProminent"
  | "fade.neutral"
  | "fade.unknown"
  | "advanced.vectorScale"
  | "advanced.ellipse.shipSigma"
  | "advanced.ellipse.airSigma"
  | "advanced.ellipse.unknownAirSigma"
  | "advanced.dropLine.minZoom"
  | "advanced.dropLine.minAltitudeM"
  | "advanced.dropLine.groundDotOpacity"
  | "advanced.dropLine.lineOpacity"
  | "alerts.readIds"
  | "alerts.clearedIds"
  | "user.callsign"
  | "user.useRealName"
  | "presence.shareLocation"
  | "presence.updateSeconds";

/** Maps each leaf path to the value type its setter accepts (for the strongly-typed setSetting). */
export interface SettingValueByPath {
  renderProfile: RenderProfileId;
  markerMode: MarkerMode;
  layout: LayoutRecipe;
  reducedMotion: boolean;
  iconDensityLod: boolean;
  blueForceAlwaysProminent: boolean;
  "fade.neutral": number;
  "fade.unknown": number;
  "advanced.vectorScale": number;
  "advanced.ellipse.shipSigma": number;
  "advanced.ellipse.airSigma": number;
  "advanced.ellipse.unknownAirSigma": number;
  "advanced.dropLine.minZoom": number;
  "advanced.dropLine.minAltitudeM": number;
  "advanced.dropLine.groundDotOpacity": number;
  "advanced.dropLine.lineOpacity": number;
  "alerts.readIds": string[];
  "alerts.clearedIds": string[];
  "user.callsign": string;
  "user.useRealName": boolean;
  "presence.shareLocation": boolean;
  "presence.updateSeconds": number;
}

/** A runtime validator for one leaf path: accepts a candidate value iff it is the right type and
 *  in range. Used by setSetting (reject unknown/invalid) AND by load-time sanitisation. */
export interface PathSpec {
  /** True iff `v` is an acceptable value for this path. */
  readonly accepts: (v: unknown) => boolean;
  /** IDENTITY-SCOPED leaf: the value is per-user state that must NEVER land in the device
   *  scope (a device-scope write would bleed one user's state into every user via the
   *  device-fallback merge). The store DROPS a write with no active persona, and load-time
   *  sanitisation DROPS such a leaf found in a persisted blob's device scope. */
  readonly identityScoped?: true;
}

const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const inUnit = (v: unknown): v is number => isFiniteNumber(v) && v >= 0 && v <= 1;
const nonNegative = (v: unknown): v is number => isFiniteNumber(v) && v >= 0;
/** Alert-ack id array: non-empty-string ids, bounded to ALERT_ACK_CAP (the validator half of the
 *  cap; the host write-site truncates to the most recent CAP as the other half). */
const isAckIdArray = (v: unknown): v is string[] =>
  Array.isArray(v) &&
  v.length <= ALERT_ACK_CAP &&
  v.every((x) => typeof x === "string" && x.length > 0);
/** Control / line-breaking chars a display handle must never carry: C0 (U+0000-U+001F),
 *  DEL + C1 (U+007F-U+009F), and the Unicode line/paragraph separators (U+2028/U+2029). */
// eslint-disable-next-line no-control-regex
const CALLSIGN_FORBIDDEN = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
/** Callsign: a plain printable string <= CALLSIGN_MAX_LENGTH ("" = unset is valid). */
const isCallsign = (v: unknown): v is string =>
  typeof v === "string" && v.length <= CALLSIGN_MAX_LENGTH && !CALLSIGN_FORBIDDEN.test(v);
/** Presence update interval: a finite number of seconds inside the accepted range. */
const isPresenceInterval = (v: unknown): v is number =>
  isFiniteNumber(v) && v >= PRESENCE_UPDATE_MIN_S && v <= PRESENCE_UPDATE_MAX_S;

/** The complete leaf-path → validator registry. A path NOT present here is an UNKNOWN key and is
 *  rejected by setSetting (typed). This is the runtime half of SettingPath/SettingValueByPath. */
export const SETTING_PATHS: Readonly<Record<SettingPath, PathSpec>> = Object.freeze({
  renderProfile: { accepts: (v) => typeof v === "string" && v.length > 0 },
  markerMode: { accepts: (v) => v === "milsym" || v === "dot" || v === "coalition" },
  layout: { accepts: (v) => v === "cockpit" || v === "field" || v === "workbench" },
  reducedMotion: { accepts: (v) => typeof v === "boolean" },
  iconDensityLod: { accepts: (v) => typeof v === "boolean" },
  blueForceAlwaysProminent: { accepts: (v) => typeof v === "boolean" },
  "fade.neutral": { accepts: inUnit },
  "fade.unknown": { accepts: inUnit },
  "advanced.vectorScale": { accepts: nonNegative },
  "advanced.ellipse.shipSigma": { accepts: nonNegative },
  "advanced.ellipse.airSigma": { accepts: nonNegative },
  "advanced.ellipse.unknownAirSigma": { accepts: nonNegative },
  "advanced.dropLine.minZoom": { accepts: isFiniteNumber },
  "advanced.dropLine.minAltitudeM": { accepts: nonNegative },
  "advanced.dropLine.groundDotOpacity": { accepts: inUnit },
  "advanced.dropLine.lineOpacity": { accepts: inUnit },
  "alerts.readIds": { accepts: isAckIdArray, identityScoped: true },
  "alerts.clearedIds": { accepts: isAckIdArray, identityScoped: true },
  "user.callsign": { accepts: isCallsign, identityScoped: true },
  "user.useRealName": { accepts: (v) => typeof v === "boolean", identityScoped: true },
  "presence.shareLocation": { accepts: (v) => typeof v === "boolean" },
  "presence.updateSeconds": { accepts: isPresenceInterval },
});

/** All known leaf paths (the full, stable setting vocabulary). */
export const SETTING_PATH_KEYS = Object.keys(SETTING_PATHS) as SettingPath[];

/** Is `key` a known, settable leaf path? (The runtime "unknown-key" gate.) */
export function isSettingPath(key: string): key is SettingPath {
  return Object.prototype.hasOwnProperty.call(SETTING_PATHS, key);
}
